import { useQuery } from '@apollo/client';
import { translateBackend } from '@assets/i18n/config';
import type {
  AssetClassLevel,
  Exchanges,
  FinancialProduct,
} from '@interfaces/financialProduct';
import type { Holding } from '@interfaces/stats';
import ListSubheader from '@mui/material/ListSubheader';
import type { SxProps } from '@mui/material/styles';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { UserContext, usePermission } from '@providers/userContextProvider';
import { Divider } from 'hephaestus/web';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FETCH_HOLDINGS, FETCH_SECURITIES } from './queries';
import { groupFinancialProductsByAssetClassLevel } from './utils/groupFinancialProductsByAssetClassLevel';

/** Indent only security rows; `SelectField` overwrites `sx` on children, so use `style`. */
const GROUPED_SECURITY_MENU_ITEM_STYLE = { paddingLeft: '35px' } as const;
const GROUPED_DIVIDER_SX = { my: 0.5 } as const;
const GROUPED_SUBHEADER_SX = {
  lineHeight: 2,
  fontWeight: 600,
  fontSize: '14px',
} as const;

export const SecuritySelect = ({
  setSecurity,
  value,
  label,
  size,
  disabledWith = null,
  readonly = false,
  exceptTickers = [],
  inputError,
  errorText,
  exchange,
  withAny,
  sx,
  testId = 'security-select',
  required = false,
  clearable = false,
  organizationId,
  fetchExternalProductShelfSecurities,
  subAccountId,
  assetClassLevel,
}: {
  setSecurity: (security: FinancialProduct | 'ANY' | undefined) => void;
  value: string;
  label: string;
  size?: 'small' | 'medium';
  disabledWith?: string | null;
  readonly?: boolean;
  exchange?: Exchanges;
  exceptTickers?: string[];
  inputError?: boolean;
  errorText?: string;
  withAny?: boolean;
  sx?: SxProps;
  testId?: string;
  required?: boolean;
  clearable?: boolean;
  organizationId?: string;
  fetchExternalProductShelfSecurities?: boolean;
  subAccountId?: string;
  /** When set (asset-class model portfolio flow), options are grouped under asset class subheaders. */
  assetClassLevel?: AssetClassLevel | string;
}) => {
  const { t } = useTranslation(['components', 'buildModels', 'shared']);
  const canReadFinancialProducts = usePermission('read:financial_products');
  const { activeOrganization } = useContext(UserContext);
  const filterOrganizationId = organizationId || activeOrganization?.id;

  const {
    loading: loadingHoldings,
    error: errorHoldings,
    data: holdingsData,
  } = useQuery(FETCH_HOLDINGS, {
    skip: !subAccountId,
    variables: {
      input: {
        filter: {
          objectType: 'SUB_ACCOUNT',
          objectId: subAccountId,
        },
      },
    },
  });

  const { loading, error, data } = useQuery(FETCH_SECURITIES, {
    skip: !filterOrganizationId || !!subAccountId,
    variables: {
      input: {
        filter: {
          organizationId: filterOrganizationId,
          ...(exchange ? { exchange } : {}),
        },
        pagination: { perPage: 500, sortField: 'ticker' },
      },
    },
  });

  const securitiesFromFinancialProductsQuery: FinancialProduct[] =
    fetchExternalProductShelfSecurities === false
      ? []
      : data?.fetchFinancialProducts?.financialProducts || [];

  const list = useMemo<FinancialProduct[]>(
    () =>
      subAccountId
        ? (holdingsData?.fetchHoldings?.holdings || []).flatMap(
            (h: Holding) => {
              const fp = h.financialProduct;
              return fp?.id &&
                fp.ticker &&
                fp.ticker.trim() !== '' &&
                fp.translatedName?.en?.toLowerCase() !== 'cash'
                ? [fp]
                : [];
            },
          )
        : securitiesFromFinancialProductsQuery,
    [subAccountId, holdingsData, securitiesFromFinancialProductsQuery],
  );

  const isLoading = subAccountId ? loadingHoldings : loading;
  const filteredList = useMemo(
    () =>
      exceptTickers.length === 0
        ? list
        : list.filter(
            (i) => i.id === value || !exceptTickers.includes(i.ticker ?? ''),
          ),
    [list, exceptTickers, value],
  );

  const groupedByAssetClass = useMemo(() => {
    if (assetClassLevel === undefined) return null;

    const groups = groupFinancialProductsByAssetClassLevel(
      filteredList,
      assetClassLevel,
      t('buildModels:other'),
    );

    return groups.length > 0 ? groups : null; // no buckets → flat list
  }, [assetClassLevel, filteredList, t]);

  if (error || errorHoldings) return <>Error</>;

  return (
    <>
      {canReadFinancialProducts ? (
        <SelectField
          testId={`${testId}-select-field`}
          value={data || holdingsData ? value : ''}
          label={label}
          fullWidth
          required={required}
          clearable={clearable}
          sx={sx}
          error={inputError}
          errorText={errorText}
          onChange={(e: { target: { value: string } }) => {
            const val = e.target.value;

            if (val === '') {
              setSecurity(undefined);
              return;
            }

            if (withAny && val === 'ANY') {
              setSecurity('ANY');
              return;
            }

            setSecurity(filteredList.find((f) => f.id === val));
          }}
          size={size}
          disabled={disabledWith !== null || readonly}
        >
          {isLoading && (
            <MenuItem disabled data-testid={`${testId}-loading`}>
              <em>{t('shared:loading')}</em>
            </MenuItem>
          )}

          {!isLoading && disabledWith && (
            <MenuItem
              key={value}
              value={value}
              data-testid={`${testId}-disabled-with`}
            >
              <strong>{disabledWith}</strong>
            </MenuItem>
          )}

          {!isLoading && !disabledWith && withAny && (
            <MenuItem key='ANY' value='ANY' data-testid={`${testId}-any`}>
              {t('components:any')}
            </MenuItem>
          )}

          {!isLoading &&
            !disabledWith &&
            (groupedByAssetClass
              ? groupedByAssetClass.flatMap((group, groupIndex) => [
                  ...(groupIndex > 0
                    ? [
                        <Divider
                          key={`divider-${group.groupKey}`}
                          sx={GROUPED_DIVIDER_SX}
                        />,
                      ]
                    : []),
                  <ListSubheader
                    key={`subheader-${group.groupKey}`}
                    disableSticky
                    tabIndex={-1}
                    sx={GROUPED_SUBHEADER_SX}
                  >
                    {group.headerLabel}
                  </ListSubheader>,
                  ...group.products.map((item: FinancialProduct) => (
                    <MenuItem
                      key={item.id}
                      value={item.id}
                      data-testid={`${testId}-${item.id}`}
                      style={GROUPED_SECURITY_MENU_ITEM_STYLE}
                    >
                      <strong>{item.ticker}</strong> -{' '}
                      {translateBackend(item.translatedName)}
                    </MenuItem>
                  )),
                ])
              : filteredList.map((item: FinancialProduct) => (
                  <MenuItem
                    key={item.id}
                    value={item.id}
                    data-testid={`${testId}-${item.id}`}
                  >
                    <strong>{item.ticker}</strong> -{' '}
                    {translateBackend(item.translatedName)}
                  </MenuItem>
                )))}
        </SelectField>
      ) : (
        <></>
      )}
    </>
  );
};

export default SecuritySelect;
